import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CATEGORIES } from "../lib/constants";
import { ServiceCard } from "../components/ServiceCard";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Handshake, Loader2, Plus, Search, X, ShieldAlert } from "lucide-react";

const EMPTY = { title: "", description: "", categories: ["groceries"], rate: "", availability: "" };

export default function Services() {
  const { user } = useAuth();
  const [services, setServices] = useState([]);
  const [mine, setMine] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);

  const eligible = user && ["runner", "both", "admin"].includes(user.role) && user.verified;
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleCategory = (v) => setForm((f) => ({
    ...f,
    categories: f.categories.includes(v) ? f.categories.filter((c) => c !== v) : [...f.categories, v],
  }));

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (cat !== "all") params.category = cat;
      if (q.trim()) params.q = q.trim();
      const reqs = [api.get("/services", { params })];
      if (eligible) reqs.push(api.get("/services/mine"));
      const res = await Promise.all(reqs);
      setServices(res[0].data);
      if (eligible) setMine(res[1].data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cat]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.categories.length === 0) return toast.error("Pick at least one category");
    setBusy(true);
    try {
      if (editingId) {
        await api.put(`/services/${editingId}`, form);
        toast.success("Service updated!");
      } else {
        await api.post("/services", form);
        toast.success("Service listed!");
      }
      setForm(EMPTY);
      setEditingId(null);
      setShowForm(false);
      await load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const startEdit = (s) => {
    setForm({ title: s.title, description: s.description, categories: s.categories?.length ? s.categories : [s.category], rate: s.rate || "", availability: s.availability || "" });
    setEditingId(s.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY);
  };

  const remove = async (id) => {
    try {
      await api.delete(`/services/${id}`);
      toast.success("Service removed");
      await load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <div className="max-w-6xl mx-auto px-5 py-8">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Handshake className="text-primary" size={26} />
            <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tight text-[#1A1D20]">Services</h1>
          </div>
          <p className="text-[#6B7280]">Neighbors offering to help — browse what runners are open to doing.</p>
        </div>
        {eligible && (
          <Button data-testid="offer-service-btn" onClick={() => (showForm ? cancelForm() : setShowForm(true))}
            className="rounded-full font-bold px-6 bg-primary hover:bg-[#FF8D70] active:scale-95 transition-transform">
            {showForm ? <><X size={18} className="mr-2" /> Close</> : <><Plus size={18} className="mr-2" /> Offer a service</>}
          </Button>
        )}
      </div>

      {user && !eligible && (
        <div data-testid="services-gate-note" className="bg-[#FFF7ED] border border-[#FCE3C4] rounded-2xl p-4 mb-6 flex items-start gap-2.5 text-sm text-[#9A6212]">
          <ShieldAlert size={17} className="mt-0.5 shrink-0" />
          <span>Only <strong>verified runners</strong> can list services. Get verified by a neighbor and set your role to Runner to start offering help.</span>
        </div>
      )}

      <AnimatePresence>
        {showForm && eligible && (
          <motion.form initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
            onSubmit={submit} data-testid="service-form"
            className="overflow-hidden bg-white rounded-3xl border border-gray-100 shadow-soft mb-8">
            <div className="p-6 sm:p-8 space-y-5">
              <div>
                <Label className="font-semibold text-sm">Service title</Label>
                <Input data-testid="service-title-input" value={form.title} onChange={upd("title")} required placeholder="Dog walking & pet sitting"
                  className="mt-1.5 rounded-2xl h-12 border-gray-200" />
              </div>
              <div>
                <Label className="font-semibold text-sm">What you offer</Label>
                <Textarea data-testid="service-description-input" value={form.description} onChange={upd("description")} required rows={3}
                  placeholder="Reliable, friendly help. Share what you can do, experience, anything neighbors should know."
                  className="mt-1.5 rounded-2xl border-gray-200" />
              </div>
              <div>
                <Label className="font-semibold text-sm mb-1.5 block">Category <span className="text-[#9CA3AF] font-normal">(pick one or more)</span></Label>
                <div className="flex flex-wrap gap-2">
                  {CATEGORIES.map((c) => (
                    <button type="button" key={c.value} data-testid={`service-cat-${c.value}`} onClick={() => toggleCategory(c.value)}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border-2 transition-colors ${form.categories.includes(c.value) ? "border-primary bg-primary/5 text-primary" : "border-gray-200 text-[#6B7280]"}`}>
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="font-semibold text-sm">Rate <span className="text-[#9CA3AF] font-normal">(optional)</span></Label>
                  <Input data-testid="service-rate-input" value={form.rate} onChange={upd("rate")} placeholder="$15/hr or from $10"
                    className="mt-1.5 rounded-2xl h-12 border-gray-200" />
                </div>
                <div>
                  <Label className="font-semibold text-sm">Availability <span className="text-[#9CA3AF] font-normal">(optional)</span></Label>
                  <Input data-testid="service-availability-input" value={form.availability} onChange={upd("availability")} placeholder="Weekday evenings, weekends"
                    className="mt-1.5 rounded-2xl h-12 border-gray-200" />
                </div>
              </div>
              <Button data-testid="service-submit-btn" type="submit" disabled={busy}
                className="w-full rounded-full h-12 font-bold bg-primary hover:bg-[#FF8D70] active:scale-95 transition-transform">
                {busy ? <Loader2 className="animate-spin" /> : editingId ? "Save changes" : "List my service"}
              </Button>
            </div>
          </motion.form>
        )}
      </AnimatePresence>

      {eligible && mine.length > 0 && (
        <div className="mb-8">
          <h2 className="font-heading font-extrabold text-xl text-[#1A1D20] mb-3">Your services ({mine.length})</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {mine.map((s, i) => <ServiceCard key={s.id} service={s} index={i} canEdit onEdit={startEdit} canDelete onDelete={remove} />)}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="relative flex-1">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
          <Input data-testid="service-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search services…"
            className="pl-11 rounded-full h-12 border-gray-200" />
        </form>
      </div>
      <div className="flex flex-wrap gap-2 mb-6">
        {[{ value: "all", label: "All" }, ...CATEGORIES].map((c) => (
          <button key={c.value} data-testid={`service-filter-${c.value}`} onClick={() => setCat(c.value)}
            className={`px-4 py-2 rounded-full text-sm font-semibold border-2 transition-colors ${cat === c.value ? "border-primary bg-primary/5 text-primary" : "border-gray-200 text-[#6B7280]"}`}>
            {c.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20 text-[#6B7280]"><Loader2 className="animate-spin" /></div>
      ) : services.length === 0 ? (
        <div data-testid="services-empty" className="bg-white rounded-2xl border border-dashed border-gray-200 p-12 text-center text-[#9CA3AF]">
          No services listed yet. {eligible ? "Be the first to offer help!" : "Check back soon."}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="services-grid">
          {services.map((s, i) => <ServiceCard key={s.id} service={s} index={i} currentUserId={user?.id} />)}
        </div>
      )}
    </div>
  );
}
